import React from 'react';
import {
  Box, Grid, Typography, Snackbar, Alert, TextField, MenuItem, Button, Chip, Paper,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { PageHeader, EntityDrawer, ConfirmDialog, SkeletonCards, ErrorState, PermissionState, FieldRow } from '../../ui';
import PlanCard from './PlanCard';
import PlanCompareMatrix from './PlanCompareMatrix';
import useGAPlansPage, { MAX_ACTIVE_PLANS } from './useGAPlansPage';
import { grantExpiryTone } from './planUtils';

const TOP_FEATURE_KEYS = ['products.max', 'users.seats', 'invoices.issued.monthly', 'reporting'];

const GAPlans = () => {
  const p = useGAPlansPage();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  if (p.status === 'loading') return <SkeletonCards rows={4} />;
  if (p.status === 'error') {
    return (
      <ErrorState
        title={p.t("Couldn't load plans", { defaultValue: "Couldn't load plans" })}
        description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
        request={p.errorInfo?.request}
        retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
        onRetry={p.load}
      />
    );
  }
  if (p.status === 'permission') {
    return <PermissionState title={p.t('ga.error.forbidden')} description={p.t('Ask a platform operator for access.', { defaultValue: 'Ask a platform operator for access.' })} />;
  }

  const topFeaturesFor = (plan) => TOP_FEATURE_KEYS
    .filter((key) => p.featuresByKey[key])
    .map((key) => ({ feature: p.featuresByKey[key], entitlement: plan.entitlements.find((e) => e.featureKey === key) }));

  return (
    <Box>
      <PageHeader
        title={p.t('Plans & Pricing', { defaultValue: 'Plans & Pricing' })}
        subtitle={p.t('The tiers every company on SmartInventory chooses from · drag a card to reorder', { defaultValue: 'The tiers every company on SmartInventory chooses from · drag a card to reorder' })}
        actions={[
          ...(p.billing.stripeEnabled ? [{
            label: p.t('Reconcile with Stripe', { defaultValue: 'Reconcile with Stripe' }), icon: SyncOutlinedIcon, variant: 'outlined', onClick: p.reconcile, disabled: p.busy,
          }] : []),
          {
            label: p.t('New plan', { defaultValue: 'New plan' }), icon: AddIcon, variant: 'contained', onClick: p.openCreate,
            disabled: !p.canCreate,
            disabledReason: p.t('You already have {{max}} active plans. Archive one to add another.', { max: MAX_ACTIVE_PLANS, defaultValue: `You already have ${MAX_ACTIVE_PLANS} active plans. Archive one to add another.` }),
          },
        ]}
      />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={p.handleDragEnd}>
        <SortableContext items={p.publicPlans.map((pl) => pl._id)} strategy={horizontalListSortingStrategy}>
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
            {p.publicPlans.map((plan) => (
              <Grid item xs={12} sm={6} lg={3} key={plan._id}>
                <PlanCard plan={plan} sortable t={p.t} topFeatures={topFeaturesFor(plan)} onEdit={() => p.goToBuilder(plan)} />
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, justifyContent: 'flex-end' }}>
                  {!plan.isDefault && (
                    <Button size="small" variant="text" startIcon={<StarOutlineIcon fontSize="small" />} onClick={() => p.setConfirm({ kind: 'default', plan })}>
                      {p.t('Recommend', { defaultValue: 'Recommend' })}
                    </Button>
                  )}
                  {plan.isDefault && (
                    <Button size="small" variant="text" color="inherit" startIcon={<StarBorderOutlinedIcon fontSize="small" />} onClick={() => p.setConfirm({ kind: 'clear_default', plan })}>
                      {p.t('Remove badge', { defaultValue: 'Remove badge' })}
                    </Button>
                  )}
                  {!plan.isDefault && (
                    <Button size="small" variant="text" color="inherit" startIcon={<ArchiveOutlinedIcon fontSize="small" />} onClick={() => p.setConfirm({ kind: 'archive', plan })}>
                      {p.t('Archive', { defaultValue: 'Archive' })}
                    </Button>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </SortableContext>
      </DndContext>

      {p.archivedPlans.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {p.t('Archived ({{count}})', { count: p.archivedPlans.length, defaultValue: `Archived (${p.archivedPlans.length})` })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {p.archivedPlans.map((plan) => (
              <Chip
                key={plan._id}
                label={`${plan.name} · ${p.t('{{count}} companies', { count: plan.subscriberCount ?? 0, defaultValue: `${plan.subscriberCount ?? 0} companies` })}`}
                onDelete={p.canCreate ? () => p.restore(plan) : undefined}
                deleteIcon={<UnarchiveOutlinedIcon />}
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>{p.t('Compare plans', { defaultValue: 'Compare plans' })}</Typography>
      <PlanCompareMatrix features={p.features} plans={p.publicPlans} t={p.t} />

      {p.privatePlans.length > 0 && (
        <Box sx={{ mt: 3 }} data-testid="operator-plans">
          <Typography variant="subtitle1">{p.t('Operator-only plans', { defaultValue: 'Operator-only plans' })}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {p.t('Never shown to companies. Assigned only by you, from a company’s page, with a reference and an optional expiry.', { defaultValue: 'Never shown to companies. Assigned only by you, from a company’s page, with a reference and an optional expiry.' })}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
            {p.privatePlans.map((plan) => (
              <Grid item xs={12} sm={6} lg={3} key={plan._id}>
                <PlanCard plan={plan} t={p.t} topFeatures={topFeaturesFor(plan)} onEdit={() => p.goToBuilder(plan)} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {(p.privatePlans.length > 0 || p.grants.length > 0) && (
        <Paper variant="outlined" sx={{ mt: 2, overflowX: 'auto' }} data-testid="partner-grants">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <HandshakeOutlinedIcon fontSize="small" color="secondary" />
            <Typography variant="subtitle1">{p.t('Partner grants', { defaultValue: 'Partner grants' })}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
              {p.t('{{count}} active', { count: p.grants.length, defaultValue: `${p.grants.length} active` })}
            </Typography>
          </Box>
          {p.grants.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {p.t('No partner grants yet. Grant one from a company’s page.', { defaultValue: 'No partner grants yet. Grant one from a company’s page.' })}
            </Typography>
          ) : (
            <Table size="small" aria-label={p.t('Partner grants', { defaultValue: 'Partner grants' })}>
              <TableHead>
                <TableRow>
                  <TableCell>{p.t('Company', { defaultValue: 'Company' })}</TableCell>
                  <TableCell>{p.t('Plan', { defaultValue: 'Plan' })}</TableCell>
                  <TableCell>{p.t('Reference', { defaultValue: 'Reference' })}</TableCell>
                  <TableCell>{p.t('Expires', { defaultValue: 'Expires' })}</TableCell>
                  <TableCell>{p.t('Since', { defaultValue: 'Since' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {p.grants.map((g) => {
                  const tone = grantExpiryTone(g.validUntil);
                  return (
                    <TableRow key={g._id} hover data-testid={`grant-row-${g.companyId}`}>
                      <TableCell>
                        <Link to={`/ga/companies/${g.companyId}`} style={{ color: 'inherit' }}>{g.companyName || g.companyId}</Link>
                      </TableCell>
                      <TableCell>{g.planSlug}</TableCell>
                      <TableCell>{g.grantReference}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={tone === 'critical' ? 'error' : tone === 'warning' ? 'warning' : 'default'}
                          label={g.validUntil
                            ? p.t('{{date}} · {{days}} days', { date: new Date(g.validUntil).toLocaleDateString(), days: g.daysRemaining ?? '', defaultValue: `${new Date(g.validUntil).toLocaleDateString()} · ${g.daysRemaining ?? ''} days` })
                            : p.t('No expiry', { defaultValue: 'No expiry' })}
                        />
                      </TableCell>
                      <TableCell>{g.startedAt ? new Date(g.startedAt).toLocaleDateString() : '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <EntityDrawer
        open={p.drawerOpen}
        onClose={p.closeCreate}
        title={p.t('New plan', { defaultValue: 'New plan' })}
        subtitle={p.t('Name and price now — features next, in the builder', { defaultValue: 'Name and price now — features next, in the builder' })}
        closeLabel={p.t('Close', { defaultValue: 'Close' })}
        cancelLabel={p.t('ga.common.cancel')}
        primaryLabel={p.saving ? p.t('Creating...') : p.t('Create & open builder', { defaultValue: 'Create & open builder' })}
        onPrimary={p.handleCreate}
        primaryDisabled={p.saving || !p.isFormValid}
        loading={p.saving}
      >
        <FieldRow label={p.t('Plan name', { defaultValue: 'Plan name' })} required error={p.formErrors.name}>
          <TextField fullWidth value={p.form.name} onChange={(e) => p.setForm((f) => ({ ...f, name: e.target.value }))} inputProps={{ maxLength: 40, 'aria-label': p.t('Plan name', { defaultValue: 'Plan name' }) }} />
        </FieldRow>
        <FieldRow label={p.t('Description', { defaultValue: 'Description' })}>
          <TextField fullWidth multiline minRows={2} value={p.form.description} onChange={(e) => p.setForm((f) => ({ ...f, description: e.target.value }))} inputProps={{ maxLength: 160 }} />
        </FieldRow>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <FieldRow label={p.t('Monthly price', { defaultValue: 'Monthly price' })} error={p.formErrors.price} sx={{ flex: 1 }}>
            <TextField fullWidth value={p.form.price} onChange={(e) => p.setForm((f) => ({ ...f, price: e.target.value }))} inputProps={{ inputMode: 'decimal', 'aria-label': p.t('Monthly price', { defaultValue: 'Monthly price' }) }} />
          </FieldRow>
          <FieldRow label={p.t('Currency', { defaultValue: 'Currency' })} sx={{ width: 120 }}>
            <TextField select fullWidth value={p.form.currency} onChange={(e) => p.setForm((f) => ({ ...f, currency: e.target.value }))}>
              {['USD', 'CAD', 'EUR', 'GBP', 'TRY'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </FieldRow>
        </Box>
      </EntityDrawer>

      <ConfirmDialog
        open={Boolean(p.confirm)}
        onClose={() => p.setConfirm(null)}
        tone={p.confirm?.kind === 'archive' || p.confirm?.kind === 'clear_default' ? 'danger' : 'primary'}
        title={({
          archive: p.t('Archive {{name}}?', { name: p.confirm?.plan?.name, defaultValue: `Archive ${p.confirm?.plan?.name}?` }),
          default: p.t('Recommend {{name}}?', { name: p.confirm?.plan?.name, defaultValue: `Recommend ${p.confirm?.plan?.name}?` }),
          clear_default: p.t('Remove the recommendation?', { defaultValue: 'Remove the recommendation?' }),
          reconcile_fix: p.t('{{count}} subscriptions differ from Stripe', { count: p.confirm?.report?.drift?.length ?? 0, defaultValue: `${p.confirm?.report?.drift?.length ?? 0} subscriptions differ from Stripe` }),
        })[p.confirm?.kind] || ''}
        description={({
          archive: p.t('Companies already on it keep their features; nobody new can choose it. You can restore it later.', { defaultValue: 'Companies already on it keep their features; nobody new can choose it. You can restore it later.' }),
          default: p.t('Shows a “Recommended” badge on this plan on the pricing page. It never grants access: a company without a subscription has no features until it subscribes.', { defaultValue: 'Shows a “Recommended” badge on this plan on the pricing page. It never grants access: a company without a subscription has no features until it subscribes.' }),
          clear_default: p.t('No plan will carry the “Recommended” badge. Nothing else changes.', { defaultValue: 'No plan will carry the “Recommended” badge. Nothing else changes.' }),
          reconcile_fix: p.t("Apply Stripe's view to these subscriptions? Stripe is the system of record for payments; this rewrites the local rows to match.", { defaultValue: "Apply Stripe's view to these subscriptions? Stripe is the system of record for payments; this rewrites the local rows to match." }),
        })[p.confirm?.kind] || ''}
        records={p.confirm?.kind === 'reconcile_fix' ? (p.confirm.report?.drift || []).map((d) => `${d.companyId || '?'} · ${d.stripeSubscriptionId} · ${(d.issues || []).join(', ')}`) : undefined}
        cancelLabel={p.t('ga.common.cancel')}
        confirmLabel={({
          archive: p.t('Archive', { defaultValue: 'Archive' }),
          default: p.t('Recommend', { defaultValue: 'Recommend' }),
          clear_default: p.t('Remove badge', { defaultValue: 'Remove badge' }),
          reconcile_fix: p.t('Apply', { defaultValue: 'Apply' }),
        })[p.confirm?.kind] || ''}
        onConfirm={p.confirmAction}
        loading={p.busy}
      />

      <Snackbar open={p.snackbar.open} autoHideDuration={4000} onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>{p.snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GAPlans;
